import React, { useEffect, useRef, useState } from 'react';
import { Box, Alert, CircularProgress } from '@mui/material';
import { READY_PLAYER_ME_CONFIG } from '../../config/readyPlayerMe';

interface ReadyPlayerMeIframeProps {
  userId: string;
  lockedAssets?: string[];
  onAvatarExported?: (avatarUrl: string, avatarId: string) => void;
  onClose?: () => void;
}

const ReadyPlayerMeIframe: React.FC<ReadyPlayerMeIframeProps> = ({
  userId,
  lockedAssets = [],
  onAvatarExported,
  onClose
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      console.log('ReadyPlayerMe iframe message received:', event.origin, event.data);
      
      // Check if message is from ReadyPlayerMe
      if (event.origin !== 'https://wakarunning.readyplayer.me') return;
      
      try {
        const data = JSON.parse(event.data);
        console.log('ReadyPlayerMe iframe event:', data);
        
        if (data.source === 'readyplayerme') {
          switch (data.eventName) {
            case 'v1.frame.ready':
              console.log('ReadyPlayerMe iframe ready');
              setIsLoading(false);
              
              // Subscribe to avatar export events
              if (iframeRef.current?.contentWindow) {
                iframeRef.current.contentWindow.postMessage(
                  JSON.stringify({
                    target: 'readyplayerme',
                    type: 'subscribe',
                    eventName: 'v1.avatar.exported',
                  }),
                  'https://wakarunning.readyplayer.me'
                );
              }
              break;
              
            case 'v1.avatar.exported':
              console.log('Avatar exported from iframe:', data.data);
              if (onAvatarExported && data.data.url && data.data.avatarId) {
                onAvatarExported(data.data.url, data.data.avatarId);
              }
              break;
              
            case 'v1.user.authorized':
              console.log('User authorized in iframe:', data.data);
              break;
              
            default:
              console.log('ReadyPlayerMe iframe event:', data);
          }
        }
      } catch (error) {
        console.log('Non-JSON message from ReadyPlayerMe iframe:', event.data);
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onAvatarExported]);

  const buildIframeUrl = () => {
    let url = `${READY_PLAYER_ME_CONFIG.baseUrl}/avatar?frameApi&userId=${userId}`;
    
    // Add locked assets parameter if provided
    if (lockedAssets.length > 0) {
      const params = new URLSearchParams();
      lockedAssets.forEach(assetId => {
        params.append('lockedAssets', assetId);
      });
      url += `&${params.toString()}`;
    }
    
    return url;
  };

  return (
    <Box sx={{ width: '100%', height: '600px', position: 'relative' }}>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 1,
          }}
        >
          <CircularProgress size={40} />
          <Box sx={{ mt: 2, fontSize: '14px', color: 'text.secondary' }}>
            Loading ReadyPlayerMe Avatar Creator...
          </Box>
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <iframe
        ref={iframeRef}
        src={buildIframeUrl()}
        width="100%"
        height="100%"
        frameBorder="0"
        allow="camera *; microphone *"
        onLoad={() => {
          console.log('ReadyPlayerMe iframe loaded');
          setIsLoading(false);
        }}
        onError={() => {
          console.error('ReadyPlayerMe iframe failed to load');
          setError('Failed to load ReadyPlayerMe Avatar Creator. Please try again.');
          setIsLoading(false);
        }}
        style={{
          border: 'none',
          borderRadius: '8px',
        }}
      />
    </Box>
  );
};

export default ReadyPlayerMeIframe;
